import { useEffect } from 'react';

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  process.env.REACT_APP_PUBLIC_URL ||
  'https://med900.com'
).replace(/\/+$/, '');

export const DEFAULT_SITE_NAME = 'Med900';
export const DEFAULT_TITLE = 'Med900 | UCAT Practice Platform';
export const DEFAULT_DESCRIPTION =
  'Data-driven UCAT preparation with percentile analytics, exam-accurate mocks, time analysis, study guides, and targeted practice.';
export const DEFAULT_IMAGE = '/higgsfield/hero.png';

export function canonicalPath(path = '/') {
  if (!path) return '/';
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return '/';
    }
  }
  const trimmed = String(path).replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function absoluteUrl(path = '/') {
  if (/^https?:\/\//i.test(path) && !path.startsWith(SITE_URL)) return path;
  const normalised = canonicalPath(path);
  return normalised === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalised}`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      element.removeAttribute(key);
    } else {
      element.setAttribute(key, String(value));
    }
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function normaliseJsonLd(schema) {
  if (!schema) return null;
  const { '@context': _context, ...rest } = schema;
  return rest;
}

export function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  robots = 'index, follow',
  keywords,
  jsonLd = [],
}) {
  const canonicalUrl = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);
  const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  useEffect(() => {
    document.title = title;

    upsertLink('canonical', canonicalUrl);

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[name="author"]', { name: 'author', content: 'Med900 Ltd' });
    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    } else {
      document.head.querySelector('meta[name="keywords"]')?.remove();
    }

    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: DEFAULT_SITE_NAME });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_AU' });

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    document.head
      .querySelectorAll('script[type="application/ld+json"][data-med900-seo="true"]')
      .forEach((node) => node.remove());

    const graph = schemas.map(normaliseJsonLd).filter(Boolean);
    if (graph.length) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.med900Seo = 'true';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': graph,
      });
      document.head.appendChild(script);
    }
  }, [canonicalUrl, description, imageUrl, keywords, robots, schemas, title, type]);

  return null;
}

export function organizationSchema() {
  return {
    '@type': 'Organization',
    '@id': absoluteUrl('/#organization'),
    name: 'Med900',
    legalName: 'Med900 Ltd',
    url: absoluteUrl('/'),
    logo: absoluteUrl('/brand/med900-lockup.svg'),
    email: 'support@med900.com',
  };
}

export function productOfferSchema() {
  return {
    '@type': 'Product',
    '@id': absoluteUrl('/pricing#product'),
    name: 'Med900 UCAT ANZ Preparation',
    description:
      'Full Med900 UCAT ANZ platform access with 20,000 active questions, 25 full mocks, percentile analytics, timing analysis, and study guides.',
    brand: { '@id': absoluteUrl('/#organization') },
    url: absoluteUrl('/pricing'),
    image: absoluteUrl(DEFAULT_IMAGE),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'AUD',
      lowPrice: '14.99',
      highPrice: '74.99',
      offerCount: '5',
      url: absoluteUrl('/pricing'),
      availability: 'https://schema.org/InStock',
    },
  };
}

export function websiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': absoluteUrl('/#website'),
    name: 'Med900',
    url: absoluteUrl('/'),
    publisher: { '@id': absoluteUrl('/#organization') },
    inLanguage: 'en-AU',
  };
}

export function webPageSchema({ path, name, description, breadcrumbs = [] }) {
  const url = absoluteUrl(path);
  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { '@id': absoluteUrl('/#website') },
    about: { '@id': absoluteUrl('/#software') },
    breadcrumb: breadcrumbs.length ? { '@id': `${url}#breadcrumb` } : undefined,
    inLanguage: 'en-AU',
  };
}

export function breadcrumbSchema(items) {
  if (!items?.length) return null;
  const url = absoluteUrl(items[items.length - 1].path || '/');
  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function softwareApplicationSchema() {
  return {
    '@type': 'SoftwareApplication',
    '@id': absoluteUrl('/#software'),
    name: 'Med900',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: absoluteUrl('/'),
    image: absoluteUrl(DEFAULT_IMAGE),
    description: DEFAULT_DESCRIPTION,
    publisher: { '@id': absoluteUrl('/#organization') },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'AUD',
      lowPrice: '14.99',
      highPrice: '74.99',
      offerCount: '5',
      url: absoluteUrl('/pricing'),
    },
  };
}

export function faqPageSchema(faqs) {
  if (!faqs?.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

export function itemListSchema({ name, description, items = [] }) {
  return {
    '@type': 'ItemList',
    name,
    description,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title || item.name,
      url: absoluteUrl(item.path || item.url),
      description: item.description,
    })),
  };
}

export function articleSchema(guide) {
  if (!guide) return null;
  const path = `/guides/${guide.slug}`;
  return {
    '@type': 'Article',
    '@id': `${absoluteUrl(path)}#article`,
    headline: guide.title,
    description: guide.description,
    url: absoluteUrl(path),
    image: absoluteUrl(`/guide-images/${guide.slug}.webp`),
    dateModified: guide.last_reviewed,
    datePublished: guide.created_at || guide.last_reviewed,
    author: { '@id': absoluteUrl('/#organization') },
    publisher: { '@id': absoluteUrl('/#organization') },
    mainEntityOfPage: { '@id': `${absoluteUrl(path)}#webpage` },
    articleSection: guide.category,
    keywords: guide.tags?.join(', '),
    inLanguage: 'en-AU',
  };
}
